import { useCallback, useEffect } from "react";

export interface CoordinatesValue {
  direction?: string;
  degrees?: string;
  minutes?: string;
  seconds?: string;
}

export interface GpsData {
  CoordinatesFormat: string;
  Latitude: CoordinatesValue | number;
  Longitude: CoordinatesValue | number;
}

export const COORDINATE_FORMATS = {
  DECIMAL_DEGREES: "Decimal degrees",
  DEGREES_MINUTES_SECONDS: "Degrees, minutes, seconds",
  DEGREES_DECIMAL_MINUTES: "Degrees, decimal minutes",
} as const;

export const COORDINATE_LIMITS = {
  LATITUDE: { min: -90, max: 90 },
  LONGITUDE: { min: -180, max: 180 },
  DEGREES: { min: 0, max: 360 },
  MINUTES: { min: 0, max: 59.999999 },
  SECONDS: { min: 0, max: 59.999999 },
} as const;

export const convertDMS2DD = (
  degrees: string | number,
  direction: string,
  minutes: string | number,
  seconds: string | number = "0"
): number => {
  const deg = Number(degrees) || 0;
  const min = Number(minutes) || 0;
  const sec = Number(seconds) || 0;
  let decimalDegrees = deg + min / 60 + sec / 3600;
  if (direction === "S" || direction === "W") decimalDegrees = -decimalDegrees;
  return Math.round(decimalDegrees * 1e6) / 1e6;
};

export const convertDD2DMS = (decimalDegrees: number, isLongitude = false) => {
  const direction = isLongitude
    ? decimalDegrees >= 0
      ? "E"
      : "W"
    : decimalDegrees >= 0
      ? "N"
      : "S";
  const abs = Math.abs(decimalDegrees);
  const degrees = Math.floor(abs);
  const minutesFromDecimal = (abs - degrees) * 60;
  const minutes = Math.floor(minutesFromDecimal);
  const seconds =
    Math.round((minutesFromDecimal - minutes) * 60 * 1000) / 1000;
  return { degrees, minutes, seconds, direction };
};

export const formatDecimal = (val: number, decimals = 3) => {
  if (typeof val !== "number" || isNaN(val)) return "0";
  const rounded =
    Math.round(val * Math.pow(10, decimals)) / Math.pow(10, decimals);
  return rounded % 1 === 0 ? String(rounded) : rounded.toFixed(decimals);
};

export const convertCoordinates = (
  currentData: GpsData,
  newFormat: string
): GpsData => {
  const { Latitude, Longitude } = currentData;

  switch (newFormat) {
    case COORDINATE_FORMATS.DECIMAL_DEGREES: {
      const latDD =
        typeof Latitude === "object" && Latitude
          ? convertDMS2DD(
              Latitude.degrees || "0",
              Latitude.direction || "N",
              Latitude.minutes || "0",
              Latitude.seconds || "0"
            )
          : Number(Latitude) || 0;
      const lngDD =
        typeof Longitude === "object" && Longitude
          ? convertDMS2DD(
              Longitude.degrees || "0",
              Longitude.direction || "E",
              Longitude.minutes || "0",
              Longitude.seconds || "0"
            )
          : Number(Longitude) || 0;
      return {
        CoordinatesFormat: newFormat,
        Latitude: latDD,
        Longitude: lngDD,
      };
    }

    case COORDINATE_FORMATS.DEGREES_MINUTES_SECONDS: {
      const latDMS =
        typeof Latitude === "object" && Latitude
          ? { ...Latitude, seconds: Latitude.seconds ?? "0" }
          : (() => {
              const c = convertDD2DMS(Number(Latitude) || 0, false);
              return {
                direction: c.direction,
                degrees: String(c.degrees),
                minutes: String(c.minutes),
                seconds: String(c.seconds),
              };
            })();

      const lngDMS =
        typeof Longitude === "object" && Longitude
          ? { ...Longitude, seconds: Longitude.seconds ?? "0" }
          : (() => {
              const c = convertDD2DMS(Number(Longitude) || 0, true);
              return {
                direction: c.direction,
                degrees: String(c.degrees),
                minutes: String(c.minutes),
                seconds: String(c.seconds),
              };
            })();

      return {
        CoordinatesFormat: newFormat,
        Latitude: latDMS,
        Longitude: lngDMS,
      };
    }

    case COORDINATE_FORMATS.DEGREES_DECIMAL_MINUTES: {
      const latDMM =
        typeof Latitude === "object" && Latitude
          ? {
              direction: Latitude.direction,
              degrees: Latitude.degrees,
              minutes: String(
                Number(Latitude.minutes || 0) +
                  (Latitude.seconds ? Number(Latitude.seconds) / 60 : 0)
              ),
            }
          : (() => {
              const c = convertDD2DMS(Number(Latitude) || 0, false);
              return {
                direction: c.direction,
                degrees: String(c.degrees),
                minutes: String(c.minutes + c.seconds / 60),
              };
            })();

      const lngDMM =
        typeof Longitude === "object" && Longitude
          ? {
              direction: Longitude.direction,
              degrees: Longitude.degrees,
              minutes: String(
                Number(Longitude.minutes || 0) +
                  (Longitude.seconds ? Number(Longitude.seconds) / 60 : 0)
              ),
            }
          : (() => {
              const c = convertDD2DMS(Number(Longitude) || 0, true);
              return {
                direction: c.direction,
                degrees: String(c.degrees),
                minutes: String(c.minutes + c.seconds / 60),
              };
            })();

      return {
        CoordinatesFormat: newFormat,
        Latitude: latDMM,
        Longitude: lngDMM,
      };
    }

    default:
      return currentData;
  }
};

export const validateCoordinates = (data: GpsData): string[] => {
  const errors: string[] = [];
  const { CoordinatesFormat, Latitude, Longitude } = data;

  if (!CoordinatesFormat) errors.push("Coordinates format is required");

  // Latitude
  if (typeof Latitude === "number") {
    if (
      Latitude < COORDINATE_LIMITS.LATITUDE.min ||
      Latitude > COORDINATE_LIMITS.LATITUDE.max
    ) {
      errors.push(
        `Latitude must be between ${COORDINATE_LIMITS.LATITUDE.min} and ${COORDINATE_LIMITS.LATITUDE.max}`
      );
    }
  } else if (Latitude) {
    if (!Latitude.direction || !Latitude.degrees || !Latitude.minutes) {
      errors.push("Latitude direction, degrees, and minutes are required");
    }
    const deg = Number(Latitude.degrees);
    const min = Number(Latitude.minutes);
    const sec = Number(Latitude.seconds || 0);
    if (deg > 90) errors.push("Latitude degrees cannot exceed 90");
    if (min >= 60) errors.push("Latitude minutes must be less than 60");
    if (sec >= 60) errors.push("Latitude seconds must be less than 60");
  }

  // Longitude
  if (typeof Longitude === "number") {
    if (
      Longitude < COORDINATE_LIMITS.LONGITUDE.min ||
      Longitude > COORDINATE_LIMITS.LONGITUDE.max
    ) {
      errors.push(
        `Longitude must be between ${COORDINATE_LIMITS.LONGITUDE.min} and ${COORDINATE_LIMITS.LONGITUDE.max}`
      );
    }
  } else if (Longitude) {
    if (!Longitude.direction || !Longitude.degrees || !Longitude.minutes) {
      errors.push("Longitude direction, degrees, and minutes are required");
    }
    const deg = Number(Longitude.degrees);
    const min = Number(Longitude.minutes);
    const sec = Number(Longitude.seconds || 0);
    if (deg > 180) errors.push("Longitude degrees cannot exceed 180");
    if (min >= 60) errors.push("Longitude minutes must be less than 60");
    if (sec >= 60) errors.push("Longitude seconds must be less than 60");
  }

  return errors;
};

type UseGpsFormParams = {
  value: GpsData;
  onChange: (data: GpsData) => void;
  disabled?: boolean;
  elements: any[];
  onValidationChange?: (isValid: boolean, errors: string[]) => void;
  formatLocation?: FormatLocation;
};

export const useGpsCoordinatesForm = ({
  value,
  onChange,
  disabled = false,
  elements,
  onValidationChange,
  formatLocation,
}: UseGpsFormParams) => {
  const {
    convertCoordinates,
    convertDMS2DD,
    formatDecimal,
    validateCoordinates,
    getFormattedCoordinates,
  } = useGpsCoordinates(formatLocation);

  const validationErrors = useCallback(
    () => validateCoordinates(value),
    [validateCoordinates, value]
  );

  const handleFormatChange = useCallback(
    (newFormat: any) => {
      if (disabled) return;
      const converted = convertCoordinates(value, newFormat.value);
      onChange(converted);
    },
    [convertCoordinates, disabled, onChange, value]
  );

  const handleCoordinateChange = useCallback(
    (coordinateType: "Latitude" | "Longitude", nextValue: any) => {
      if (disabled) return;
      const next: GpsData = { ...value, [coordinateType]: nextValue };
      onChange(next);
    },
    [disabled, onChange, value]
  );

  const handleDMSChange = useCallback(
    (coordinateType: "Latitude" | "Longitude", part: string, val: any) => {
      if (disabled) return;

      const sanitizedVal = String(val).replace(/-/g, "");
      const current = (value[coordinateType] as CoordinatesValue) || {};
      const candidate: CoordinatesValue = { ...current, [part]: sanitizedVal };

      const deg = Number(candidate.degrees || 0);
      const min = Number(candidate.minutes || 0);
      const sec = Number(candidate.seconds || 0);

      if (
        part === "degrees" &&
        deg > (coordinateType === "Latitude" ? 90 : 180)
      )
        return;
      if (part === "minutes" && min >= 60) return;
      if (part === "seconds" && sec >= 60) return;

      const minutesInDegrees = min / 60;
      const secondsInDegrees = sec / 3600;
      let totalDegrees = deg + minutesInDegrees + secondsInDegrees;
      if (candidate.direction === "S" || candidate.direction === "W")
        totalDegrees = -totalDegrees;
      const limit = coordinateType === "Latitude" ? 90 : 180;
      if (Math.abs(totalDegrees) > limit) return;

      if (
        value.CoordinatesFormat === COORDINATE_FORMATS.DEGREES_DECIMAL_MINUTES
      ) {
        delete candidate.seconds;
      }

      onChange({ ...value, [coordinateType]: candidate });
    },
    [disabled, onChange, value]
  );

  const formatChoices = useCallback(() => {
    try {
      const el = elements.find((e: any) => e?.name === "CoordinatesFormat");
      return el?.choices || [];
    } catch {
      return [];
    }
  }, [elements]);

  const coordinatesPlaceholders = useCallback(() => {
    const choices = formatChoices();
    return choices.map((f: any) => {
      const formatValue = f?.value || f;
      const display =
        formatValue === COORDINATE_FORMATS.DEGREES_MINUTES_SECONDS
          ? "DMS"
          : formatValue === COORDINATE_FORMATS.DECIMAL_DEGREES
            ? "DD"
            : "DMM";
      return getFormattedCoordinates(
        value.Latitude,
        value.Longitude,
        display as any
      );
    });
  }, [formatChoices, getFormattedCoordinates, value.Latitude, value.Longitude]);

  // Push validation state to consumer
  useEffect(() => {
    const errors = validationErrors();
    onValidationChange?.(errors.length === 0, errors);
  }, [onValidationChange, validationErrors]);

  return {
    convertDMS2DD,
    formatDecimal,
    validationErrors: validationErrors(),
    handleFormatChange,
    handleCoordinateChange,
    handleDMSChange,
    formatChoices: formatChoices(),
    coordinatesPlaceholders: coordinatesPlaceholders(),
  };
};

type FormatLocation = (
  value: any,
  format: "DMS" | "DD" | "DMM",
  isLongitude?: boolean,
  displayFormat?: any
) => string | number;

/**
 * Shared GPS conversion/validation helpers wrapped for React usage.
 * Optionally accepts a `formatLocation` helper for UI-friendly strings.
 */
const useGpsCoordinates = (formatLocation?: FormatLocation) => {
  const getFormattedCoordinates = useCallback(
    (latitude: any, longitude: any, format: "DMS" | "DD" | "DMM"): string => {
      if (!latitude || !longitude) return "Coordinates not available";

      try {
        switch (format) {
          case "DMS":
            if (typeof latitude !== "object" || typeof longitude !== "object") {
              return formatLocation
                ? `${formatLocation(latitude, format, false, "DDMS")}, ${formatLocation(longitude, format, true, "DDMS")}`
                : "Invalid coordinates";
            }
            return `${latitude.direction} ${formatDecimal(Number(latitude.degrees))}° ${formatDecimal(Number(latitude.minutes))}' ${formatDecimal(Number(latitude.seconds || 0))}", ${longitude.direction} ${formatDecimal(Number(longitude.degrees))}° ${formatDecimal(Number(longitude.minutes))}' ${formatDecimal(Number(longitude.seconds || 0))}"`;

          case "DMM":
            if (typeof latitude !== "object" || typeof longitude !== "object") {
              return formatLocation
                ? `${formatLocation(latitude, format, false, "DDMM")}, ${formatLocation(longitude, format, true, "DDMM")}`
                : "Invalid coordinates";
            }
            const latMin =
              Number(latitude.minutes) +
              (latitude.seconds ? Number(latitude.seconds) / 60 : 0);
            const lonMin =
              Number(longitude.minutes) +
              (longitude.seconds ? Number(longitude.seconds) / 60 : 0);
            return `${latitude.direction} ${formatDecimal(Number(latitude.degrees))}° ${formatDecimal(latMin)}', ${longitude.direction} ${formatDecimal(Number(longitude.degrees))}° ${formatDecimal(lonMin)}'`;

          case "DD":
            return "Pick on map / get GPS data";

          default:
            return "Unknown format";
        }
      } catch {
        return "Error formatting coordinates";
      }
    },
    [formatLocation]
  );

  return {
    COORDINATE_FORMATS,
    COORDINATE_LIMITS,
    convertCoordinates,
    convertDMS2DD,
    convertDD2DMS,
    formatDecimal,
    validateCoordinates,
    getFormattedCoordinates,
  };
};

export default useGpsCoordinates;
